import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import apiService from "../../service/apiService";


const CropPlan = () => {
  const [rows, setRows] = useState([]);
  const [pgOptions, setPgOptions] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [allFarmers, setAllFarmers] = useState([]);
  const [farmerOptions, setFarmerOptions] = useState([]); // State to store filtered farmers
  const [selectedPgId, setSelectedPgId] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [newState, setNewState] = useState({
    seasonId: "",
    yearId: "",
    farmerId: "",
  });
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  
  // Fetch producer groups for dropdown
  const fetchPgOptions = async () => {
    try {
      const response = await apiService.get("/producer-groups/get-all");
      const options = response.data.map(item => ({
        label: item.pgname,
        value: item.producerGroupId,
      }));
      setPgOptions(options);
    } catch (error) {
      console.error("Error fetching PG options:", error);
    }
  };

  // Fetch all farmers and store in state
  const fetchFarmers = async () => {
    try {
      const response = await apiService.get("/farmers/get-all-farmers");
      setAllFarmers(response.data); // Store all farmers for filtering later
    } catch (error) {
      console.error("Error fetching farmer options:", error);
    }
  };

  // Fetch seasons for dropdown
  const fetchSeasonOptions = async () => {
    try {
      const response = await apiService.get("/seasons/get-all");
      const options = response.data.map(item => ({
        label: item.seasonName,
        value: item.seasonId,
      }));
      setSeasonOptions(options);
    } catch (error) {
      console.error("Error fetching season options:", error);
    }
  };

  // Fetch years for dropdown
  const fetchYears = async () => {
    try {
      const response = await apiService.get("/years/get-all");
      const options = response.data.map(item => ({
        label: item.yearValue.toString(),
        value: item.yearId,
      }));
      setYearOptions(options);
    } catch (error) {
      console.error("Error fetching year options:", error);
    }
  };

  // Filter farmers based on selected producer group
  const filterFarmersByPgId = (pgid) => {
    const filteredFarmers = allFarmers
      .filter(farmer => farmer.pgid === pgid) // Filter farmers based on selected PG ID
      .map(farmer => ({
        label: farmer.farmerName, // Use farmerName as the label
        value: farmer.farmerId, // Use farmerId as the value
      }));
    setFarmerOptions(filteredFarmers); // Update the farmerOptions state
  };

  // Handle PG selection change
  useEffect(() => {
    if (selectedPgId) {
      filterFarmersByPgId(selectedPgId);
      fetchPgData(selectedPgId);
    } else {
      setFarmerOptions([]); // Clear farmer options if no PG is selected
    }
  }, [selectedPgId, allFarmers]);
  

  // Fetch data for the selected producer group
  const fetchPgData = async (producerGroupId) => {
    try {
      const response = await apiService.get(`/producer-groups/get-by-pg-id/${producerGroupId}`);
      const formattedData = response.data.map(item => ({
        id: item.farmerId,
        farmerName: item.farmerName,
        age: item.age,
        phoneNumber: item.phoneNumber,
        registeredDate: item.registeredDate,
        createdBy: item.createdBy,
        createdAt: item.createdAt,
        isActive: item.isActive,
        updatedBy: item.updatedBy,
        updatedAt: item.updatedAt,
      }));
      setRows(formattedData);
    } catch (error) {
      console.error("Error fetching PG data:", error);
    }
  };

  useEffect(() => {
    fetchPgOptions();
    fetchSeasonOptions();
    fetchYears();
    fetchFarmers();
  }, []);

  const handleClickOpenAdd = () => {
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setNewState({
      seasonId: "",
      yearId: "",
      farmerId: "",
    });
  };



  const handleSaveAdd = async () => {
    try {
      await apiService.post("/crop-plan-master/create-demo", {
        seasonId: newState.seasonId,
        yearId: newState.yearId,
        farmerId: newState.farmerId,
      });
      console.log("Crop plan data saved:", newState);
      handleCloseAdd();
    } catch (error) {
      console.error("Error saving crop plan data:", error);
    }
  };
 

  const columns = [
    { field: "farmerName", headerName: "Farmer Name", flex: 1 },
    { field: "age", headerName: "Age", flex: 1 },
    { field: "phoneNumber", headerName: "Phone Number", flex: 1 },
    { field: "registeredDate", headerName: "Registered Date", flex: 1, type: "date" },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton>
            <VisibilityIcon style={{ color: colors.greenAccent[500] }} />
          </IconButton>
          <IconButton>
            <EditIcon style={{ color: colors.blueAccent[600] }} />
          </IconButton>
          <IconButton>
            <DeleteIcon style={{ color: colors.redAccent[600] }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header title="Crop Planning Management" subtitle="List of crop plans" />
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Autocomplete
          options={pgOptions}
          getOptionLabel={(option) => option.label}
          onChange={(event, newValue) => setSelectedPgId(newValue ? newValue.value : null)}
          renderInput={(params) => (
            <TextField {...params} label="Select PG" variant="outlined" sx={{ width: 200, marginRight: 2 }} />
          )}
        />
        <Button variant="contained" color="primary" onClick={handleClickOpenAdd}>
          Create Plan
        </Button>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.id}
        />

       {/* Add Crop Dialog */}
<Dialog 
  open={openAdd} 
  onClose={handleCloseAdd} 
  maxWidth="sm" 
  fullWidth
  PaperProps={{
    style: {
      borderRadius: '16px',  // Rounded corners for the dialog box
      padding: '20px',  // Add padding inside the dialog for better spacing
    }
  }}
>
  <DialogTitle sx={{ backgroundColor: "#1976d2", color: "white", padding: "16px", fontSize: "20px", fontWeight: "bold", borderRadius: "8px 8px 0 0" }}>
    Add Crop plan
  </DialogTitle>
  <DialogContent sx={{ padding: "20px" }}>
    
    {/* Season Dropdown */}
    <Autocomplete
              options={seasonOptions}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => setNewState({ ...newState, seasonId: newValue ? newValue.value : "" })}
              renderInput={(params) => (
                <TextField {...params} label="Select Season" variant="outlined" fullWidth margin="dense" sx={{ marginBottom: "16px", borderRadius: "8px" }} />
              )}
            />
    {/* Year Dropdown */}
    <Autocomplete
              options={yearOptions}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => setNewState({ ...newState, yearId: newValue ? newValue.value : "" })}
              renderInput={(params) => (
                <TextField {...params} label="Select Year" variant="outlined" fullWidth margin="dense" sx={{ marginBottom: "16px", borderRadius: "8px" }} />
              )}
            />

    
    {/* Farmer Dropdown */}
    <Autocomplete
              options={farmerOptions}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => setNewState({ ...newState, farmerId: newValue ? newValue.value : "" })}
              renderInput={(params) => (
                <TextField {...params} label="Select Farmer" variant="outlined" fullWidth margin="dense" sx={{ marginBottom: "16px", borderRadius: "8px" }} />
              )}
            />
</DialogContent>
          <Divider />
          <DialogActions sx={{ padding: "20px", justifyContent: "center" }}>
            <Button onClick={handleCloseAdd} variant="outlined" color="primary">Cancel</Button>
            <Button onClick={handleSaveAdd} variant="contained" color="primary">Save</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default CropPlan;
